/* eslint-disable max-statements */
/**
 * Generic PDF Generator
 * Provides reusable utilities for generating PDFs using @react-pdf/renderer
 * Includes base A4 page styles and common configurations
 */

import { pdf, StyleSheet } from "@react-pdf/renderer";

/**
 * Options for generating a PDF
 */
export interface PdfGeneratorOptions {
  /** File name for the downloaded PDF */
  fileName: string;
  /** Optional callback to report progress updates */
  onProgress?: (message: string) => void;
}

/**
 * Base A4 page styles
 * Common styles that can be extended by specific PDF generators
 */
export const baseA4Styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 11,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  header: {
    backgroundColor: "#f5f5f5",
    padding: 15,
    marginBottom: 20,
    borderRadius: 8,
    border: "1 solid #e0e0e0",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#1976d2",
  },
  subtitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#424242",
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#424242",
    marginBottom: 12,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  sectionLabel: {
    fontWeight: "bold",
    fontSize: 11,
    color: "#424242",
    marginBottom: 8,
  },
  contentBox: {
    backgroundColor: "#fafafa",
    padding: 12,
    marginBottom: 12,
    borderRadius: 4,
    border: "1 solid #e0e0e0",
  },
  text: {
    fontSize: 10,
    lineHeight: 1.6,
    color: "#212121",
  },
  metadataRow: {
    flexDirection: "row",
    marginBottom: 6,
    fontSize: 10,
  },
  metadataLabel: {
    fontWeight: "bold",
    color: "#424242",
    marginRight: 5,
  },
  metadataValue: {
    color: "#616161",
  },
});

/**
 * Generate a PDF blob from a React-PDF Document component without triggering download.
 */
export async function generatePdfBlob(
  document: React.ReactElement,
): Promise<Blob> {
  return pdf(document).toBlob();
}

/**
 * Generate and download a PDF from a React-PDF Document component
 *
 * @param document - The React-PDF Document component to render
 * @param options - Options for PDF generation
 */
export async function generatePdf(
  document: React.ReactElement,
  options: PdfGeneratorOptions,
): Promise<void> {
  const { fileName, onProgress } = options;

  try {
    onProgress?.("Converting to PDF...");

    // Generate blob
    const pdfBlob = await pdf(document).toBlob();

    onProgress?.("Downloading PDF...");

    // Create download link and trigger download
    const url = URL.createObjectURL(pdfBlob);
    const link = globalThis.document.createElement("a");
    link.href = url;
    link.download = fileName;
    globalThis.document.body.appendChild(link);
    link.click();
    link.remove();

    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);

    onProgress?.("PDF downloaded!");
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  }
}

/**
 * HTML Helper Functions
 */

/**
 * Decode HTML entities to plain text
 */
export function decodeHtmlEntities(text: string): string {
  return text
    .replaceAll(/&nbsp;/g, " ")
    .replaceAll(/&amp;/g, "&")
    .replaceAll(/&lt;/g, "<")
    .replaceAll(/&gt;/g, ">")
    .replaceAll(/&quot;/g, '"')
    .replaceAll(/&#39;/g, "'")
    .replaceAll(/&apos;/g, "'")
    .replaceAll(/&mdash;/g, "—")
    .replaceAll(/&ndash;/g, "–")
    .replaceAll(/&hellip;/g, "…");
}

/**
 * Strip HTML tags and convert to plain text with preserved structure
 * Handles tables, lists, and common formatting
 */
export function stripHtml(html: string): string {
  if (!html) return "";

  let text = html;

  // Remove style tags and their content
  text = text.replaceAll(/<style[^>]*>[\s\S]*?<\/style>/gi, "");

  // Remove script tags and their content
  text = text.replaceAll(/<script[^>]*>[\s\S]*?<\/script>/gi, "");

  // Handle tables specially to preserve structure
  text = text.replaceAll(
    /<table[^>]*>([\s\S]*?)<\/table>/gi,
    (_match, tableContent) => {
      let tableText = "\n";

      // Extract rows
      const rows: string[] = [];
      tableContent.replaceAll(
        /<tr[^>]*>([\s\S]*?)<\/tr>/gi,
        (_rowMatch: string, rowContent: string) => {
          const cells: string[] = [];

          // Extract cells (th or td)
          rowContent.replaceAll(
            /<t[hd][^>]*>([\s\S]*?)<\/t[hd]>/gi,
            (_cellMatch: string, cellContent: string) => {
              // Remove HTML tags from cell content
              let cellText = cellContent.replaceAll(/<[^>]*>/g, "").trim();
              // Decode entities
              cellText = decodeHtmlEntities(cellText);
              cells.push(cellText);
              return "";
            },
          );

          if (cells.length > 0) {
            // Join cells with tab-like spacing using multiple spaces
            rows.push(cells.join("    "));
          }
          return "";
        },
      );

      tableText += rows.join("\n") + "\n\n";
      return tableText;
    },
  );

  // Convert common block elements to newlines
  text = text.replaceAll(/<\/p>/gi, "\n\n");
  text = text.replaceAll(/<br\s*\/?>/gi, "\n");
  text = text.replaceAll(/<\/div>/gi, "\n");
  text = text.replaceAll(/<\/h[1-6]>/gi, "\n\n");

  // Convert list items to bullets
  text = text.replaceAll(/<li[^>]*>/gi, "\n• ");
  text = text.replaceAll(/<\/li>/gi, "");

  // Add spacing after lists
  text = text.replaceAll(/<\/ul>/gi, "\n");
  text = text.replaceAll(/<\/ol>/gi, "\n");

  // Remove all remaining HTML tags
  text = text.replaceAll(/<[^>]*>/g, "");

  // Decode HTML entities
  text = decodeHtmlEntities(text);

  // Clean up excessive whitespace but preserve paragraph breaks
  text = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("\n\n");

  return text.trim();
}
